// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { ListCard } from '@/components/greenlight/ListCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export type DataListColumnPriority = 'md' | 'lg' | 'xl';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

export interface DataListColumn<T> {
  key: string;
  header: string;
  primary?: boolean;
  priority?: DataListColumnPriority;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  className?: string;
}

/**
 * Which copy of the list a callback is rendering for. Both views are always
 * mounted and CSS shows one, so anything that needs a unique id (an expanded
 * panel and the `aria-controls` naming it) must include the view in that id.
 */
export type DataListView = 'table' | 'cards';

export interface DataListProps<T> {
  items: T[];
  columns: DataListColumn<T>[];
  rowKey: (item: T) => string;
  rowAction?: (item: T, view: DataListView) => React.ReactNode;
  empty?: React.ReactNode;
  className?: string;
  /** Accessible name for the table element. */
  ariaLabel?: string;
  /**
   * Total rows in the underlying result set, for `aria-rowcount`. Defaults to
   * the number of rendered items when omitted. Ignored with `renderExpanded`,
   * which leaves row numbering off.
   */
  totalRowCount?: number;
  /** Current sort, reflected as `aria-sort` on the matching header. */
  sort?: SortState | null;
  /** Called with a column key when a sortable header is activated. */
  onSortChange?: (key: string) => void;
  /**
   * When set, the whole mobile card becomes a single tap target that calls this
   * with the row's item (matching the design's "card is the button" pattern).
   * On desktop, `rowAction` remains the affordance.
   */
  onRowActivate?: (item: T) => void;
  /**
   * Optional custom renderer for the mobile card body. When provided it replaces
   * the default primary-name + label/value layout, letting a page match a bespoke
   * card design (e.g. status in the corner + a two-column field grid). The card
   * tap target / click-vs-drag handling is still owned by DataList.
   */
  renderCard?: (item: T) => React.ReactNode;
  /**
   * Rendered as a sibling column beside the mobile card's tap-target button
   * (never nested inside it — a nested interactive control there would be
   * invalid button-in-button markup, since `onRowActivate` makes the whole
   * card one `<button>`). Lets a page offer per-row interactive controls (e.g.
   * a listing switch) alongside `onRowActivate` on mobile.
   */
  cardActions?: (item: T) => React.ReactNode;
  /** Extra classes to append to a given row (desktop TableRow and mobile card). */
  rowClassName?: (item: T) => string | undefined;
  /** The `rowKey` value of the currently-selected row. */
  selectedKey?: string;
  /**
   * Multi-select: when set, a leading checkbox column is rendered (a `<td>` on
   * desktop, a sibling of the mobile activation button — never nested inside it,
   * which would be invalid button-in-button markup). `selectedKeys` holds the
   * checked `rowKey` values; `onToggleRow` flips one. Toggling never triggers
   * `onRowActivate` (the checkbox stops propagation).
   */
  selectedKeys?: ReadonlySet<string>;
  onToggleRow?: (item: T) => void;
  /** Accessible label for a row's checkbox; defaults to "Select row". */
  selectionLabel?: (item: T) => string;
  /**
   * Detail shown under a row when it returns non-null: a full-width row on
   * desktop, below the card on mobile. The page owns which rows are expanded
   * and the toggle control (usually `rowAction`), and gives the returned
   * element the id that control's `aria-controls` names, suffixed with `view`
   * so the table and card copies don't share it.
   */
  renderExpanded?: (item: T, view: DataListView) => React.ReactNode;
}

function stopSelectionActivation(e: React.SyntheticEvent): void {
  e.stopPropagation();
}

// Column visibility uses viewport breakpoints so column drops line up with the
// app shell's own breakpoints (e.g. the sidebar going full → icon-rail at `lg`).
// Pages choose conservative priorities so the table still never needs a
// horizontal scroll even while the sidebar narrows the content area.
function priorityClass(priority?: DataListColumnPriority): string {
  switch (priority) {
    case 'md':
      return 'hidden md:table-cell';
    case 'lg':
      return 'hidden lg:table-cell';
    case 'xl':
      return 'hidden xl:table-cell';
    default:
      return '';
  }
}

function renderCell<T>(column: DataListColumn<T>, item: T): React.ReactNode {
  if (column.render) return column.render(item);
  const raw = (item as Record<string, unknown>)[column.key];
  if (raw == null) return null;
  return raw as React.ReactNode;
}

function ariaSortValue<T>(
  column: DataListColumn<T>,
  sort: SortState | null | undefined,
): React.AriaAttributes['aria-sort'] {
  if (!column.sortable) return undefined;
  if (!sort || sort.key !== column.key) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  if (!active) return <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden />;
  return direction === 'asc' ? (
    <ChevronUp className="size-3.5" aria-hidden />
  ) : (
    <ChevronDown className="size-3.5" aria-hidden />
  );
}

export function DataList<T>({
  items,
  columns,
  rowKey,
  rowAction,
  empty,
  className,
  ariaLabel,
  totalRowCount,
  sort,
  onSortChange,
  onRowActivate,
  renderCard,
  cardActions,
  rowClassName,
  selectedKey,
  selectedKeys,
  onToggleRow,
  selectionLabel,
  renderExpanded,
}: DataListProps<T>) {
  if (items.length === 0 && empty !== undefined) {
    return <div className={cn('text-sm text-muted-foreground', className)}>{empty}</div>;
  }

  const selectable = !!onToggleRow;
  const rowChecked = (item: T): boolean => !!selectedKeys?.has(rowKey(item));
  const rowSelectionLabel = (item: T): string => selectionLabel?.(item) ?? 'Select row';

  const primaryColumn = columns.find((c) => c.primary) ?? columns[0];
  const secondaryColumns = columns.filter((c) => c !== primaryColumn);

  // Distinguish a genuine click from a text-selection drag: record the pointer-down
  // position, then on click only activate if the pointer barely moved (mouse jitter)
  // and the user isn't selecting text *within this row* — so dragging to select a
  // row's text doesn't open it, while a selection elsewhere on the page doesn't
  // block opening it.
  const downPos = React.useRef<{ x: number; y: number } | null>(null);
  const handleActivatePointerDown = (e: React.PointerEvent): void => {
    downPos.current = { x: e.clientX, y: e.clientY };
  };
  const handleActivateClick =
    (item: T) =>
    (e: React.MouseEvent): void => {
      if (!onRowActivate) return;
      const down = downPos.current;
      downPos.current = null;
      // Keyboard activation (Enter/Space on a focused card) fires a click with no
      // pointer coords (detail === 0). The drag guard below would measure it against
      // whatever pointer-down the ref last held — possibly a stale one from a drag on
      // another row — and wrongly reject it. Keyboard clicks aren't drags: activate.
      if (e.detail === 0) {
        onRowActivate(item);
        return;
      }
      const selection = typeof window !== 'undefined' ? window.getSelection() : null;
      if (selection && !selection.isCollapsed && e.currentTarget.contains(selection.anchorNode)) {
        return; // an active selection inside this row → user was selecting, not clicking
      }
      if (down) {
        const dx = e.clientX - down.x;
        const dy = e.clientY - down.y;
        if (dx * dx + dy * dy > 36) return; // moved > ~6px → a drag/selection, not a click
      }
      onRowActivate(item);
    };
  // +1 for the header row, matching the aria-rowindex values assigned below.
  // An expanded detail row adds a <tr> that the count and index can't track
  // without renumbering on every toggle, so with `renderExpanded` both are
  // left off and assistive tech falls back to DOM order.
  const numberRows = !renderExpanded;
  const rowCount = numberRows ? (totalRowCount ?? items.length) + 1 : undefined;

  return (
    <div className={className}>
      {/* md+ (sidebar present): real table, framed as a card with a hairline
          header. Columns hide on viewport breakpoints (`priority`) chosen so the
          drops line up with the shell (e.g. Auth tier leaves at `lg`, when the
          sidebar goes to icons) and the table never needs a horizontal scroll.
          Below md it collapses to the card list, in step with the sidebar →
          bottom-nav switch. */}
      <div className="hidden overflow-hidden rounded-gl-lg border border-border bg-card md:block">
        {/* Extra left padding on the leading cell so the first column (icon +
            name) isn't cramped against the table border. */}
        <Table
          aria-label={ariaLabel}
          aria-rowcount={rowCount}
          className="[&_tr>*:first-child]:pl-4"
        >
          <TableHeader>
            <TableRow
              aria-rowindex={numberRows ? 1 : undefined}
              className="border-border/60 bg-muted/50 hover:bg-muted/50"
            >
              {selectable && <TableHead className="w-8" aria-label="Select" />}
              {columns.map((column) => {
                const sortable = column.sortable && onSortChange;
                const isActive = sort?.key === column.key;
                return (
                  <TableHead
                    key={column.key}
                    aria-sort={ariaSortValue(column, sort)}
                    className={cn(
                      'whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.08em]',
                      priorityClass(column.priority),
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(column.key)}
                        aria-label={`Sort by ${column.header}, currently ${isActive ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : 'unsorted'}`}
                        className="inline-flex items-center gap-1 font-medium uppercase hover:text-foreground"
                      >
                        {column.header}
                        <SortIcon active={!!isActive} direction={sort?.direction ?? 'asc'} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                );
              })}
              {rowAction && <TableHead aria-label="Row action" />}
            </TableRow>
          </TableHeader>
          <TableBody className="[&_tr:last-child]:border-0">
            {items.map((item, index) => {
              const key = rowKey(item);
              const isSelected = selectedKey != null && key === selectedKey;
              // When onRowActivate is set without a rowAction, the <tr> itself is the
              // keyboard path (role="button" + tabIndex). When rowAction is also present,
              // the focusable control inside rowAction is the keyboard/AT path — making the
              // <tr> focusable too would create a nested-interactive violation (interactive
              // element inside interactive element), so only the mouse onClick is kept.
              const rowIsKeyboardActivable = !!onRowActivate && !rowAction;
              const expanded = renderExpanded?.(item, 'table');
              return (
                <React.Fragment key={key}>
                  <TableRow
                    aria-rowindex={numberRows ? index + 2 : undefined}
                    aria-current={isSelected ? true : undefined}
                    data-selected={isSelected ? 'true' : undefined}
                    role={rowIsKeyboardActivable ? 'button' : undefined}
                    tabIndex={rowIsKeyboardActivable ? 0 : undefined}
                    onPointerDown={onRowActivate ? handleActivatePointerDown : undefined}
                    onClick={onRowActivate ? handleActivateClick(item) : undefined}
                    onKeyDown={
                      rowIsKeyboardActivable
                        ? (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowActivate(item);
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      'border-border/60',
                      // Whole-row click target (design): row greys on hover. When rowAction
                      // is present its focusable control carries the keyboard/AT path; `group`
                      // lets that control darken on row hover. When the row itself is the
                      // keyboard path (no rowAction), a visible focus ring is also applied.
                      onRowActivate && 'group cursor-pointer hover:bg-accent/50',
                      rowIsKeyboardActivable &&
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      rowClassName?.(item),
                    )}
                  >
                    {selectable && (
                      <TableCell className="w-8" onClick={stopSelectionActivation}>
                        <span
                          className="inline-flex"
                          onClick={stopSelectionActivation}
                          onPointerDown={stopSelectionActivation}
                        >
                          <Checkbox
                            checked={rowChecked(item)}
                            onCheckedChange={() => onToggleRow?.(item)}
                            aria-label={rowSelectionLabel(item)}
                          />
                        </span>
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn(priorityClass(column.priority), column.className)}
                      >
                        {renderCell(column, item)}
                      </TableCell>
                    ))}
                    {rowAction && (
                      <TableCell className="text-right">{rowAction(item, 'table')}</TableCell>
                    )}
                  </TableRow>
                  {expanded != null && (
                    <TableRow className="border-border/60 hover:bg-transparent">
                      <TableCell
                        colSpan={columns.length + (selectable ? 1 : 0) + (rowAction ? 1 : 0)}
                        className="bg-muted/30"
                      >
                        {expanded}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Below md: stacked cards (in step with the sidebar → bottom-nav switch).
          When onRowActivate is set the whole card is the tap target (design "card
          is the button"). A page may pass `renderCard` for a bespoke card body. */}
      <ul className="space-y-2 md:hidden">
        {items.map((item) => {
          const key = rowKey(item);
          const isSelected = selectedKey != null && key === selectedKey;
          const body = renderCard ? (
            renderCard(item)
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {primaryColumn && (
                    <div className="text-[15px] font-semibold text-foreground">
                      {renderCell(primaryColumn, item)}
                    </div>
                  )}
                </div>
                {onRowActivate ? (
                  <ChevronRight
                    className="mt-1 size-4 shrink-0 text-muted-foreground"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                ) : (
                  rowAction && <div className="shrink-0">{rowAction(item, 'cards')}</div>
                )}
              </div>
              {secondaryColumns.length > 0 && (
                <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5">
                  {secondaryColumns.map((column) => (
                    <React.Fragment key={column.key}>
                      <dt className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                        {column.header}
                      </dt>
                      <dd className="min-w-0 truncate text-sm text-foreground">
                        {renderCell(column, item)}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </>
          );
          // The selection checkbox is a sibling of the activation button, never a
          // child — a checkbox (button) inside the card button would be invalid
          // button-in-button markup and an a11y violation.
          const checkbox = selectable ? (
            <span className="flex items-start pt-3 pl-1">
              <Checkbox
                checked={rowChecked(item)}
                onCheckedChange={() => onToggleRow?.(item)}
                aria-label={rowSelectionLabel(item)}
              />
            </span>
          ) : null;
          const card = onRowActivate ? (
            <button
              type="button"
              onPointerDown={handleActivatePointerDown}
              onClick={handleActivateClick(item)}
              className="group/card block w-full rounded-gl-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {/* The whole card greys on hover (matching the desktop row). The
                    tint goes on the ListCard itself so its bg-card doesn't cover it. */}
              <ListCard
                className={cn(
                  'transition-colors group-hover/card:bg-accent/50',
                  rowClassName?.(item),
                )}
              >
                {body}
              </ListCard>
            </button>
          ) : (
            <ListCard className={cn(rowClassName?.(item))}>{body}</ListCard>
          );
          const needsRow = selectable || !!cardActions;
          const expanded = renderExpanded?.(item, 'cards');
          return (
            <li
              key={key}
              aria-current={isSelected ? true : undefined}
              data-selected={isSelected ? 'true' : undefined}
              className={
                needsRow ? cn('flex items-start gap-1', expanded != null && 'flex-wrap') : undefined
              }
            >
              {checkbox}
              {needsRow ? <div className="min-w-0 flex-1">{card}</div> : card}
              {expanded != null && <div className="mt-2 basis-full px-1">{expanded}</div>}
              {cardActions && (
                <div
                  className="flex shrink-0 items-center gap-0.5 pt-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  {cardActions(item)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
